import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_SERIES,
  RECEIVING_SERIES,
  RECEIVE_SERIES_METADATA,
  RECEIVE_SERIES_FAILURE,
  SAVING_SERIES,
  SAVING_SERIES_FAILURE,
  UPDATING_SERIES,
  UPDATING_SERIES_FAILURE,
  DELETE_SERIES,
  DELETING_SERIES,
  DELETING_SERIES_FAILURE,
  INCREMENT_SERIES_COUNT,
  RESET_SERIES_META,
  CLEAR_SERIES_STATE,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const serieSchema = new schema.Entity('series')
const serieListSchema = new schema.Array(serieSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchSeriesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_SERIES, true)
    return http.get(buildUrlWithQuery('series', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_SERIES, false)
        this.commit(RESET_SERIES_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, serieListSchema)
          this.commit(RECEIVE_SERIES, { data: entities.series })
          this.commit(RECEIVE_SERIES_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_SERIES, false)
        this.commit(RECEIVE_SERIES_FAILURE, handleXHRError(e))
      })
  },
  fetchSeriesOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_SERIES, true)
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_SERIES, true)
    return http({ url: buildUrl('series', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_SERIES, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities } = normalize(choices, serieListSchema)
          this.commit(RECEIVE_SERIES, { data: entities.series })
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_SERIES, false)
        this.commit(RECEIVE_SERIES_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getSeries: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getSeriesMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getSeriesById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getSeriesBySearchCriteria: (state) => (query) => {
    const { data } = state
    return Object.values(data).filter(v => Object.keys(v).some(k => {
      if (v[k]) return v[k].toString().toLowerCase().includes(query.toLowerCase())
      return false
    }))
  },
  getAllSeries: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getDefaultSeries: (state) => {
    return state.cache ? Object.values(state.cache).find(v => v.active) : {}
  },
  getSeriesFetchStatus: (state) => {
    return state.receivingData
  },
  getSeriesSaveStatus: (state) => {
    return state.saving
  },
  getSeriesUpdateStatus: (state) => {
    return state.updating
  },
  getSeriesDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_SERIES] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_SERIES] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_SERIES_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_SERIES] (state, payload) {
    state.saving = payload
  },
  [SAVING_SERIES_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_SERIES] (state, payload) {
    state.updating = payload
  },
  [UPDATING_SERIES_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_SERIES] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_SERIES] (state, payload) {
    state.deleting = payload
  },
  [DELETING_SERIES_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_SERIES_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_SERIES_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_SERIES_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_SERIES_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
